import { useCallback, useState } from 'react';
import type { WsServerMessage } from '@reflex/shared';
import { useSocketMessage } from '@/hooks/socket/useSocket';

interface InternalState {
  agentId: string | undefined;
  version: string | null;
}

/**
 * Track the on-box flex-bridge version for a single agent from ephemeral
 * `sandbox:bridge` broadcasts. The server publishes the version when the box
 * says hello and clears it (`version: null`) on teardown; it also replays the
 * current value to newly registered sockets, so this resolves shortly after a
 * page load without the monitoring panel being open. Returns `null` until a
 * value is known. Surfaced in the agent detail debug area.
 */
export function useSandboxBridgeVersion(agentId: string | undefined): string | null {
  const [state, setState] = useState<InternalState>({ agentId, version: null });

  const handler = useCallback(
    (msg: WsServerMessage) => {
      if (!agentId) return;
      if (msg.type !== 'sandbox:bridge') return;
      if (msg.agentId !== agentId) return;
      setState({ agentId, version: msg.version });
    },
    [agentId],
  );

  useSocketMessage(handler);

  return state.agentId === agentId ? state.version : null;
}
